import React, { useState, useEffect } from 'react';
import { ShoppingCart, Loader2, ExternalLink, MoreVertical, Star } from 'lucide-react';
import type { Ingredient } from '../types/recipe';
import { INGREDIENT_CATEGORIES } from '../types/recipe';
import { useGroceryStore } from '../store/groceryStore';

interface GrocerySearchProps {
  ingredients: Ingredient[];
}

export function GrocerySearch({ ingredients }: GrocerySearchProps) {
  const { stores, defaultStore, loading, error, fetchStores, setDefaultStore } = useGroceryStore();
  const [searchLoading, setSearchLoading] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const handleImageError = (storeId: string) => {
    setFailedImages(prev => new Set([...prev, storeId]));
  };

  // Group ingredients by category and sort by priority
  const groupedIngredients = React.useMemo(() => {
    const groups = new Map<string, Ingredient[]>();
    ingredients.forEach(ingredient => {
      const existing = groups.get(ingredient.category) || [];
      groups.set(ingredient.category, [...existing, ingredient]);
    });
    
    return Array.from(groups.entries()).sort((a, b) => {
      const priorityA = INGREDIENT_CATEGORIES[a[0] as keyof typeof INGREDIENT_CATEGORIES]?.priority || 999;
      const priorityB = INGREDIENT_CATEGORIES[b[0] as keyof typeof INGREDIENT_CATEGORIES]?.priority || 999;
      return priorityA - priorityB;
    });
  }, [ingredients]);

  const handleSearch = async (ingredient: Ingredient, store: typeof stores[0]) => {
    setSearchLoading(true);
    setOpenMenuId(null);

    try {
      const searchTerms = [ingredient.base_ingredient]
        .filter(Boolean)
        .join(' OR ');

      if (searchTerms) {
        window.open(`${store.search_url}${encodeURIComponent(searchTerms)}`, '_blank');
      }
    } catch (err) {
      console.error('Grocery search error:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const formatIngredient = (ing: Ingredient) => {
    const parts = [];
    if (ing.amount) parts.push(ing.amount);
    if (ing.unit) parts.push(ing.unit);
    parts.push(ing.base_ingredient);
    return parts.join(' ');
  };

  const renderStoreIcon = (store: { store_id: string; store_name: string; logo_url: string | null }) => {
    if (failedImages.has(store.store_id) || !store.logo_url) {
      return (
        <div className="w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
          <span className="text-gray-500 text-xs font-medium">
            {store.store_name.charAt(0)}
          </span>
        </div>
      );
    }

    return (
      <img
        src={store.logo_url}
        alt={store.store_name}
        className="w-4 h-4 object-contain"
        onError={() => handleImageError(store.store_id)}
      />
    );
  };

  if (!ingredients?.length || !stores.length) {
    return null;
  }

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <ShoppingCart size={20} className="text-gray-600" />
          <h4 className="font-medium">Shopping List</h4>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Default Store:</span>
          <div className="relative">
            <button
              onClick={() => setOpenMenuId('default-store')}
              className="flex items-center gap-2 text-sm border rounded px-2 py-1 hover:bg-gray-50"
            >
              {defaultStore && renderStoreIcon(defaultStore)}
              <span>{defaultStore?.store_name || 'Select store'}</span>
              <Star
                size={16}
                className="text-yellow-500"
                fill={defaultStore ? 'currentColor' : 'none'}
              />
            </button>
            {openMenuId === 'default-store' && (
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                {stores.map((store) => (
                  <button
                    key={store.store_id}
                    onClick={() => {
                      setDefaultStore(store.store_id);
                      setOpenMenuId(null);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    {renderStoreIcon(store)}
                    <span>{store.store_name}</span>
                    {store.is_default && (
                      <Star size={16} className="ml-auto text-yellow-500" fill="currentColor" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {groupedIngredients.map(([category, items]) => (
          <div key={category} className="space-y-2">
            <div className="font-medium text-gray-900">
              {INGREDIENT_CATEGORIES[category as keyof typeof INGREDIENT_CATEGORIES]?.name || category}
            </div>
            <div className="space-y-2">
              {items.map((item, index) => (
                <div 
                  key={`${category}-${item.base_ingredient}-${index}`} 
                  className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-700">
                      {formatIngredient(item)}
                    </div>
                    {item.alternatives.length > 0 && (
                      <div className="text-sm text-gray-500">
                        Alternatives: {item.alternatives.join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => defaultStore && handleSearch(item, defaultStore)}
                      disabled={searchLoading || !defaultStore}
                      className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                      title={`Search ${defaultStore?.store_name}`}
                    >
                      {searchLoading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <ExternalLink size={16} />
                      )}
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === item.base_ingredient ? null : item.base_ingredient)}
                        className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <MoreVertical size={16} />
                      </button>
                      {openMenuId === item.base_ingredient && (
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                          {stores.map((store) => (
                            <button
                              key={store.store_id}
                              onClick={() => handleSearch(item, store)}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              {renderStoreIcon(store)}
                              <span>{store.store_name}</span>
                              {store.is_default && (
                                <Star size={16} className="ml-auto text-yellow-500" fill="currentColor" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}